import { Field } from "formik"
import useTranslation from "next-translate/useTranslation"
import { useEffect } from "react"
import { Button, FormGroup, FormText, Label } from "reactstrap"
import { InputType } from "reactstrap/es/Input"

import ActionIcon from "@components/common/ActionIcon"
import Icon from "@components/common/Icon"
import TranslatedHtml from "@components/common/TranslatedHtml"
import { addNamespacePrefixIfNotPresent } from "@services/i18n"

import FormikInput from "./FormikInput"


/**
 * to be used as title for a FormikSingleInputArray element
 * to be used as div>title for more information as well as for type recognition on tests, @see FormikHelper.fillForm()
 */
export const FORMIK_SINGLE_INPUT_ARRAY_TYPE_TITLE = "single field form array"

/**
 * to be used as title for the button to add a new element element
 * to be used as title for more information as well as for type recognition on tests, @see FormikHelper.fillForm()
 */
export const FORMIK_INPUT_ARRAY_ADD_BUTTON_TYPE_TITLE = "add another array field"


/**
 * This props are relevant to the component itself for showing content description, styling and react, when it's focused.
 */
export interface IArrayEditProps {
  name: string
  fieldsToShow?: string[] // which fields should be displayed/shown, important, e.g. if you have hidden fields, if none is given, all fields will be displayed
  label?: string
  help?: string
  inputType?: InputType
  placeholder?: string
  classNameAddButton?: string
  addButtonTitle: string
  addButtonClassName?: string
  addButtonSize?: string
  addButtonIconSize?: number
  onFocus?: () => void
  disabled?: boolean
  markVisuallyAsRequired?: boolean
}

/**
 * This props are functional like adding an empty element or removing an element as well as relevant for the actual content like values and error messages.
 * This separations make's it possible to generalize and individualize this component.
 *
 * Import notice on errors: the component differentiate between a general error and individual errors.
 * A general error is recognized if an error is defined (first array element) and there are no valuess added.
 *
 */
interface IFieldProps extends IArrayEditProps {
  values: string[]
  error: string[]
  onAddEmptyElement: () => void
  onRemoveElement: (index: number) => void
}

/**
 * It provides a list of InputFields, handled as an array of FormikInput
 *
 * The placeholder is optional. If no icon size for the submit button is given, the icon size will be 20.
 * If no classname for the submit button is given, it will be 'btn form-btn'.
 * If no button size is given, it will be 'sm'.
 *
 * The separation into two interfaces comes from separation from FieldArrayRenderProps and our own Array Component (FormikSingleInputArray).
 *
 * The component is focused, when the add button is pressed or an individual inputfield is selected.
 */

const FormikSingleInputArray: React.FC<IFieldProps> = ({
  name,
  fieldsToShow,
  values,
  error,
  onAddEmptyElement,
  onFocus,
  onRemoveElement,
  disabled,
  label = "",
  help = "",
  inputType = "text", // may be number?
  placeholder = "",
  addButtonTitle = "",
  addButtonClassName = "btn form-btn",
  addButtonSize = "sm",
  addButtonIconSize = 20,
  markVisuallyAsRequired
}: IFieldProps) => {
  const { t } = useTranslation("common")

  useEffect(() => {
    // if no values exist, push an empty value => so the user sees immediately the input fields
    // in other words: the default value is at least one input field
    if (!values || values.length === 0) {
      onAddEmptyElement()
    }
  }, [])

  // @todo FormikArrayTextarea
  return <FormGroup name={name} row={false} title={FORMIK_SINGLE_INPUT_ARRAY_TYPE_TITLE}>
    <Label>{markVisuallyAsRequired ? t(label) + " *" : t(label)}</Label>

    <FormText>
      <TranslatedHtml content={t(help)} />
    </FormText>

    {// display general error for the hole array-field like: "You have to add at least one value.""
      error && values.length === 0 &&
      <div className="text-danger d-block">
        <TranslatedHtml content={addNamespacePrefixIfNotPresent("error", error[0])} />
      </div>
    }

    {values && values.length > 0 && values.filter(fieldName => fieldsToShow ? fieldsToShow.includes(fieldName) : true).map((value: any, index: number) =>
      <div key={index} className="flexbox-icon-navigation-inputfields">
        <Field component={FormikInput}
          name={`${name}.${index}`} // set name of that single field: name.index
          placeholder={t(placeholder)}
          value={value}
          id={`${name}-${index}`}
          type={inputType}
          label=""
          onFocus={onFocus}
          disabled={disabled}
        />
        <div className="icon-navigation input-group-addon">
          <ActionIcon
            aria-label={t("form.removeElement")}
            title={t("form.removeElement")}
            icon={"trash"}
            onclick={() => {
              onRemoveElement(index)
            }}
            disabled={disabled}
          />
        </div>

      </div>
    )}

    <Button
      onClick={() => {
        if (onFocus) onFocus()
        onAddEmptyElement()
      }}
      className={addButtonClassName}
      size={addButtonSize}
      disabled={disabled}
      title={FORMIK_INPUT_ARRAY_ADD_BUTTON_TYPE_TITLE}
    >
      <Icon name="plus" size={addButtonIconSize} />
      {t(addButtonTitle)}
    </Button>
  </FormGroup>

}

export default FormikSingleInputArray